"use client";

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

interface Service {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  tags: string[];
  accentColor: string;
  image: string;
  imageAlt: string;
  size: 'large' | 'medium' | 'small';
}

const services: Service[] = [
  {
    id: 'electronica',
    icon: '⚡',
    title: 'Ingeniería Electrónica',
    subtitle: 'Diseño & Prototipado',
    description: 'Diseño y construcción de sistemas electrónicos a medida. Desde prototipado de circuitos hasta sistemas embebidos para aplicaciones industriales y comerciales.',
    tags: ['PCB Design', 'Sistemas Embebidos', 'Prototipado', 'Arduino/ESP32'],
    accentColor: '#07CDE1',
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'Electronic circuit board with microcontroller and copper traces for custom embedded system design',
    size: 'large',
  },
  {
    id: 'software',
    icon: '💻',
    title: 'Software & Web',
    subtitle: 'Aplicaciones a medida',
    description: 'Desarrollo de aplicaciones móviles y web intuitivas. Desde sistemas de gestión empresarial hasta plataformas e-commerce y APIs robustas.',
    tags: ['Web Apps', 'Mobile', 'APIs', 'Sistemas de Gestión'],
    accentColor: '#3B82F6',
    image: 'https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg?auto=compress&cs=tinysrgb&w=800',
    imageAlt: 'Developer working on laptop with code on screen building web application',
    size: 'medium',
  },
  {
    id: 'mantenimiento',
    icon: '🔧',
    title: 'Mantenimiento Técnico',
    subtitle: 'Preventivo & Correctivo',
    description: 'Soporte continuo para equipos electrónicos y de computación. Mantenimiento preventivo programado y atención correctiva rápida para minimizar tiempos de inactividad.',
    tags: ['Preventivo', 'Correctivo', 'Diagnóstico', 'Soporte continuo'],
    accentColor: '#94A3B8',
    image: 'https://images.pexels.com/photos/257736/pexels-photo-257736.jpeg?auto=compress&cs=tinysrgb&w=800',
    imageAlt: 'Technician performing preventive maintenance on electronic equipment with multimeter',
    size: 'small',
  },
  {
    id: 'seguridad',
    icon: '🔒',
    title: 'Seguridad y Control',
    subtitle: 'CCTV & Acceso',
    description: 'Integración de sistemas CCTV de alta definición y control de acceso biométrico. Protege tus instalaciones con tecnología de vigilancia avanzada.',
    tags: ['CCTV HD', 'Control de Acceso', 'Biometría', 'Monitoreo 24/7'],
    accentColor: '#EF4444',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'Security camera installation on wall for CCTV surveillance system',
    size: 'medium',
  },
  {
    id: 'automatizacion',
    icon: '⚙️',
    title: 'Automatización Industrial',
    subtitle: 'Industria 4.0',
    description: 'Control de procesos industriales, reducción de desperdicios y optimización de fabricación. Implementamos PLCs, HMIs y sistemas SCADA para tu planta.',
    tags: ['PLC', 'SCADA', 'HMI', 'Control de Procesos'],
    accentColor: '#22C55E',
    image: 'https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=800',
    imageAlt: 'Industrial automation control panel with PLC and SCADA system for manufacturing process',
    size: 'large',
  },
  {
    id: 'telecomunicaciones',
    icon: '📡',
    title: 'Telecomunicaciones',
    subtitle: 'Redes & Conectividad',
    description: 'Diseño e implementación de redes seguras y eficientes para oficinas y equipos conectados. Infraestructura de red estructurada, WiFi empresarial y VPN.',
    tags: ['Redes LAN/WAN', 'WiFi Empresarial', 'VPN', 'Cableado Estructurado'],
    accentColor: '#A855F7',
    image: 'https://images.pexels.com/photos/442150/pexels-photo-442150.jpeg?auto=compress&cs=tinysrgb&w=800',
    imageAlt: 'Network server rack with ethernet cables and switches for enterprise telecommunications infrastructure',
    size: 'small',
  },
  {
    id: 'impresion3d',
    icon: '🖨️',
    title: 'Diseño e Impresión 3D',
    subtitle: 'Modelado & Fabricación',
    description: 'Modelado CAD y fabricación de prototipos, piezas de repuesto y componentes personalizados con precisión milimétrica. Del diseño digital a la pieza física.',
    tags: ['CAD/CAM', 'Prototipos', 'Piezas de repuesto', 'FDM/SLA'],
    accentColor: '#EC4899',
    image: 'https://images.pexels.com/photos/3913025/pexels-photo-3913025.jpeg?auto=compress&cs=tinysrgb&w=800',
    imageAlt: '3D printer creating a prototype part with precision layer-by-layer manufacturing',
    size: 'medium',
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal-hidden');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="servicios"
      ref={sectionRef}
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{ background: 'linear-gradient(180deg, #0D1525 0%, #0A0F1A 100%)' }}
    >
      {/* Section header */}
      <div className="max-w-7xl mx-auto mb-16 reveal-hidden">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="section-label mb-4">// Unidades de negocio</p>
            <h2 className="font-heading font-800 text-4xl md:text-5xl text-white leading-tight tracking-tight">
              Nuestros{' '}
              <span className="gradient-text-cyan">Servicios</span>
            </h2>
          </div>
          <p className="font-body text-[#8892A4] text-base max-w-sm leading-relaxed md:text-right">
            7 áreas de especialización técnica, un solo punto de contacto para todas tus necesidades tecnológicas.
          </p>
        </div>

        {/* Decorative line */}
        <div className="mt-8 h-px" style={{ background: 'linear-gradient(90deg, rgba(7,205,225,0.4), rgba(7,205,225,0.05), transparent)' }} />
      </div>

      {/* Bento grid */}
      <div className="max-w-7xl mx-auto">
        {/* Row 1: Large + Medium */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Electrónica — Large */}
          <ServiceCard
            service={services[0]}
            isHovered={hoveredId === services[0].id}
            onHover={setHoveredId}
            className="lg:col-span-2 reveal-hidden"
            showImage
          />
          {/* Software — Medium */}
          <ServiceCard
            service={services[1]}
            isHovered={hoveredId === services[1].id}
            onHover={setHoveredId}
            className="reveal-hidden"
            style={{ transitionDelay: '0.1s' }}
          />
        </div>

        {/* Row 2: Small + Large + Small */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <ServiceCard
            service={services[2]}
            isHovered={hoveredId === services[2].id}
            onHover={setHoveredId}
            className="reveal-hidden"
            style={{ transitionDelay: '0.15s' }}
          />
          <ServiceCard
            service={services[4]}
            isHovered={hoveredId === services[4].id}
            onHover={setHoveredId}
            className="reveal-hidden"
            showImage
            style={{ transitionDelay: '0.2s' }}
          />
          <ServiceCard
            service={services[3]}
            isHovered={hoveredId === services[3].id}
            onHover={setHoveredId}
            className="reveal-hidden"
            style={{ transitionDelay: '0.25s' }}
          />
        </div>

        {/* Row 3: Medium + Small + Medium */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ServiceCard
            service={services[5]}
            isHovered={hoveredId === services[5].id}
            onHover={setHoveredId}
            className="reveal-hidden"
            style={{ transitionDelay: '0.3s' }}
          />
          <ServiceCard
            service={services[6]}
            isHovered={hoveredId === services[6].id}
            onHover={setHoveredId}
            className="reveal-hidden md:col-span-2"
            showImage
            style={{ transitionDelay: '0.35s' }}
          />
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="max-w-7xl mx-auto mt-12 text-center reveal-hidden" style={{ transitionDelay: '0.4s' }}>
        <a
          href="#contacto"
          className="btn-primary inline-flex items-center gap-3 px-10 py-4 rounded text-base"
        >
          ¿Trabajamos juntos?
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </a>
      </div>
    </section>
  );
}

interface ServiceCardProps {
  service: Service;
  isHovered: boolean;
  onHover: (id: string | null) => void;
  className?: string;
  showImage?: boolean;
  style?: React.CSSProperties;
}

function ServiceCard({ service, isHovered, onHover, className = '', showImage = false, style }: ServiceCardProps) {
  return (
    <div
      className={`service-card service-card-${getColorClass(service.accentColor)} rounded-xl overflow-hidden cursor-pointer ${className}`}
      onMouseEnter={() => onHover(service.id)}
      onMouseLeave={() => onHover(null)}
      style={{ ...style, '--card-accent': service.accentColor, '--card-glow': `${service.accentColor}18` } as React.CSSProperties}
    >
      {showImage && (
        <div className="relative h-44 overflow-hidden">
          <AppImage
            src={service.image}
            alt={service.imageAlt}
            fill
            className="object-cover transition-transform duration-700 group-hover:scale-105"
            style={{
              filter: 'grayscale(30%) brightness(0.6)',
              transform: isHovered ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.7s cubic-bezier(0.19, 1, 0.22, 1)',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom, transparent 40%, ${service.accentColor}15 100%)`,
            }}
          />
        </div>
      )}

      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0 service-icon-bg"
          >
            {service.icon}
          </div>
          <div className="accent-line mt-3" />
        </div>

        {/* Title */}
        <h3
          className="font-heading font-700 text-white text-xl mb-1 tracking-tight"
        >
          {service.title}
        </h3>
        <p
          className="text-[11px] font-body font-500 tracking-widest uppercase mb-3"
          style={{ color: service.accentColor }}
        >
          {service.subtitle}
        </p>

        {/* Description */}
        <p className="font-body text-[#8892A4] text-sm leading-relaxed mb-5">
          {service.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {service.tags.map((tag) => (
            <span
              key={`${service.id}-${tag}`}
              className="text-[10px] font-body font-500 px-2.5 py-1 rounded-sm"
              style={{
                background: `${service.accentColor}10`,
                border: `1px solid ${service.accentColor}25`,
                color: service.accentColor,
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* CTA link */}
        <div className="mt-5 flex items-center gap-2 group/link">
          <a
            href="#contacto"
            className="text-[11px] font-body font-600 tracking-wider uppercase transition-colors"
            style={{ color: isHovered ? service.accentColor : '#8892A4' }}
          >
            Solicitar información
          </a>
          <svg
            className="w-3 h-3 transition-transform"
            style={{
              color: isHovered ? service.accentColor : '#8892A4',
              transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function getColorClass(hex: string): string {
  const map: Record<string, string> = {
    '#07CDE1': 'cyan',
    '#3B82F6': 'blue',
    '#94A3B8': 'gray',
    '#EF4444': 'red',
    '#22C55E': 'green',
    '#A855F7': 'purple',
    '#EC4899': 'pink',
  };
  return map[hex] || 'cyan';
}